import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'

import { NAV } from '@brickup/shared/locales/de/nav'
import { ReadOnlyScope } from '@/components/base'
import { PageLayout } from '@/components/Internal/PageLayout'
import { SectionRail } from '@/components/Internal/SectionRail'
import { firstAreaOf } from '@/constants/system-navigation'
import {
  useCanChangeSystemGroup,
  useVisibleSystemGroups,
} from '@/hooks/use-system-groups'
import { longestMatch } from '@/utils/route-path'

export const Route = createFileRoute('/internal/system')({
  component: SystemLayout,
})

/**
 * System in two levels: the groups across the header, their pages down the
 * left.
 *
 * A tab leads to the group's first page, so choosing a group is one click. A
 * group marked `fullWidth` drops the area column — the configurator brings its
 * own list and pane.
 *
 * The pages sit in a read-only scope where the group admits the reader without
 * letting them change anything, so every control inside disables itself and no
 * page has to know who is looking. The rail and the tabs stay outside it: they
 * navigate, and a group nobody may change is still one to walk through.
 */
function SystemLayout() {
  const pathname = useLocation({ select: location => location.pathname })
  const groups = useVisibleSystemGroups()
  // The longest match, not the first: a group may hold an area nested under a
  // sibling's path, and first-listed-wins would hand the wrong one back.
  const activeHref = longestMatch(
    pathname,
    groups.flatMap(group => group.areas.map(area => String(area.href))),
  )
  const active = groups.find(group =>
    group.areas.some(area => String(area.href) === activeHref),
  )
  const canChange = useCanChangeSystemGroup(active)
  const page = (
    <ReadOnlyScope readOnly={!canChange}>
      <Outlet />
    </ReadOnlyScope>
  )

  return (
    <PageLayout
      title={NAV.system}
      tabs={groups.map(group => ({
        id: group.id,
        label: group.label,
        to: firstAreaOf(group),
      }))}
      activeTab={active?.id}
    >
      {active && !active.fullWidth ? (
        <div className="flex min-h-0 flex-1 flex-row">
          <SectionRail
            sections={active.areas.map(area => ({
              key: String(area.href),
              label: area.label,
              to: area.href,
            }))}
            activeKey={activeHref}
          />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {page}
          </div>
        </div>
      ) : (
        page
      )}
    </PageLayout>
  )
}
